import type { Application, CalendarEvent } from "sdk";
import {
	type Accessor,
	createContext,
	createSignal,
	type ParentComponent,
	type Setter,
	useContext,
} from "solid-js";

// TODO: add emoji menu
// TODO: add role menu

export type Menu = {
	x: number;
	y: number;
} & (
	| { type: "room"; room_id: string }
	| { type: "channel"; channel_id: string }
	| {
			type: "message";
			channel_id: string;
			message_id: string;
			version_id: string;
	  }
	| {
			type: "user";
			user_id: string;
			channel_id?: string;
			room_id?: string;
			admin: boolean;
	  }
	| { type: "settings" }
	| { type: "voice_input" }
	| { type: "voice_output" }
	| { type: "folder"; folder_id: string }
	| { type: "view"; view_id: string }
	| { type: "view_channel"; view_id: string; channel_id: string }
	| {
			type: "emoji";
			/** reaction key param, e.g. `c:{id}` for custom or `t:{unicode}` for unicode */
			key: string;
			id?: string;
			name?: string;
			content?: string;
			animated?: boolean;
	  }
	| { type: "topic"; channel_id: string }
	| { type: "voice"; channel_id: string }
	| { type: "invite_application"; app: Application }
	| {
			type: "permission_overwrite";
			channel_id: string;
			overwrite_id: string;
			overwrite_type: "Role" | "User" | "Everyone";
			onDelete?: () => void;
	  }
	| { type: "channel_nav"; room_id?: string; view_id?: string }
	| {
			type: "calendar_event";
			channel_id: string;
			event: CalendarEvent;
			anchor?: HTMLElement;
	  }
	| {
			type: "color_picker_options";
			onColorSpaceChange: (space: "oklch" | "srgb") => void;
	  }
);

export type MenuContextT = {
	menu: Accessor<Menu | null>;
	setMenu: Setter<Menu | null>;
	preview: Accessor<string | null>;
	setPreview: Setter<string | null>;
};

const MenuContext = createContext<MenuContextT>();

export const MenuProvider: ParentComponent = (props) => {
	const [menu, setMenu] = createSignal<Menu | null>(null);
	const [preview, setPreview] = createSignal<string | null>(null);

	return (
		<MenuContext.Provider value={{ menu, setMenu, preview, setPreview }}>
			{props.children}
		</MenuContext.Provider>
	);
};

export const useMenu = () => {
	const context = useContext(MenuContext);
	if (!context) {
		throw new Error("useMenu must be used within a MenuProvider");
	}
	return context;
};
